import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Close as CloseIcon,
  SwapHoriz as StatusIcon,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { QK } from "../../../base/qk";
import {
  getIncidentDetails,
  updateIncident,
} from "../../../api/services/notification";
import CustomSelect from "../../FormFields/CustomSelect/CustomSelect";
import { statusOptions } from "../incident.config";

const ChangeIncidentStatus = ({ id, open, handleClose }) => {
  const theme = useTheme();
  const [status, setStatus] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const { data: incidentDetails, isLoading } = useQuery(
    [QK.incidentDetails, id],
    () => getIncidentDetails(id),
    { enabled: !!id && open }
  );

  useEffect(() => {
    if (incidentDetails) {
      setStatus(incidentDetails.status || "");
    }
  }, [incidentDetails]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!status) {
      enqueueSnackbar("Please select a status", { variant: "warning" });
      return;
    }

    setIsUpdating(true);
    try {
      const response = await updateIncident(id, { status });

      if (response.status === 200) {
        enqueueSnackbar("Incident status updated successfully", {
          variant: "success",
        });
        queryClient.invalidateQueries(QK.incidentsList);
        queryClient.invalidateQueries([QK.incidentDetails, id]);
        handleClose();
      } else {
        enqueueSnackbar("Failed to update status", { variant: "error" });
      }
    } catch (error) {
      enqueueSnackbar("Failed to update status", { variant: "error" });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancel = () => {
    if (incidentDetails) {
      setStatus(incidentDetails.status || "");
    }
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="xs"
      fullWidth
      aria-labelledby="change-incident-status-modal"
      aria-describedby="modal-to-change-incident-status"
      PaperProps={{ sx: { borderRadius: "12px" } }}
    >
      <DialogTitle
        id="change-incident-status-modal"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pr: 1,
          pb: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "8px",
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <StatusIcon
              sx={{ fontSize: 20, color: theme.palette.primary.main }}
            />
          </Box>
          <Box>
            <Typography
              variant="h6"
              fontWeight={700}
              color="#1A1A2E"
              fontSize="1rem"
            >
              Change Incident Status
            </Typography>
            <Typography variant="caption" color="#6B7280" fontSize="0.7rem">
              Update the current status of this incident
            </Typography>
          </Box>
        </Box>
        <Button
          onClick={handleCancel}
          size="small"
          sx={{
            minWidth: 32,
            width: 32,
            height: 32,
            p: 0,
            color: "#6B7280",
            "&:hover": { bgcolor: "#F2F4F7" },
          }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </Button>
      </DialogTitle>

      <Box component="form" onSubmit={handleSave}>
        <DialogContent dividers sx={{ pt: 2.5, pb: 2.5 }}>
          {isLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 4,
              }}
            >
              <CircularProgress size={28} />
            </Box>
          ) : (
            <CustomSelect
              name="status"
              label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              options={statusOptions}
              placeholder="Select status"
              required
              fullWidth
            />
          )}
        </DialogContent>

        {!isLoading && (
          <DialogActions
            sx={{
              px: 3,
              py: 2,
              bgcolor: "#F9FAFB",
              borderTop: "1px solid #EAECF0",
            }}
          >
            <Button
              onClick={handleCancel}
              variant="outlined"
              size="small"
              disabled={isUpdating}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "8px",
                borderColor: "#D0D5DD",
                color: "#344054",
                px: 2.5,
                "&:hover": {
                  borderColor: "#98A2B3",
                  bgcolor: "#F9FAFB",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              size="small"
              disabled={isUpdating}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "8px",
                px: 2.5,
                boxShadow: "none",
                "&:hover": { boxShadow: "none" },
              }}
            >
              {isUpdating ? "Saving..." : "Save"}
            </Button>
          </DialogActions>
        )}
      </Box>
    </Dialog>
  );
};

export default ChangeIncidentStatus;